// kfe-personal-promise.js — Personal Promise screen.
//
// PORT TARGET:
//   iOS: KFELearnerPersonalPromiseVC + KFELearnerSubmittedPromiseVC
//   (Classes/KFEModules/UserSetting/PersonalPromise)
//
// The learner reads a study commitment and ticks "I agree" + "Submit".
// Once signed, the screen renders read-only with the signed-on date.
//
// Storage: two settings in learner-store —
//   `personal_promise_signed`     boolean
//   `personal_promise_signed_at`  unix-ms timestamp
//
// On submit, dispatches a `kfe:setting-changed` DOM event that the future
// sync engine (Brief 025) will pick up.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t, getLanguage } from "../core/i18n.js";
import { services } from "../platform-services.js";
import * as router from "../router.js";

export class KFEPersonalPromise extends KFEComponent {
  static styles = `
    :host { display: block; }
    .doc {
      max-height: 60vh; overflow-y: auto;
      padding: 1rem; border: 1px solid var(--kfe-border);
      border-radius: var(--kfe-radius);
      background: var(--kfe-surface);
      line-height: 1.5;
    }
    .doc h2 { margin: 0 0 0.75rem; font-size: var(--kfe-h2); text-align: center; }
    .doc p.intro { color: var(--kfe-accent); margin: 0 0 0.75rem; }
    .doc ol { margin: 0; padding-left: 1.25rem; }
    .doc ol li { margin-bottom: 0.5rem; }
    .agree-row {
      display: flex; align-items: center; gap: 0.5rem;
      margin: 1rem 0;
    }
    .agree-row input { width: 1.25rem; height: 1.25rem; }
    .actions { display: flex; gap: 0.75rem; }
    button.primary {
      padding: 0.6rem 1.25rem; border-radius: var(--kfe-radius);
      border: 1px solid var(--kfe-accent);
      background: var(--kfe-accent); color: var(--kfe-on-accent);
      font: inherit; cursor: pointer;
    }
    button.primary[disabled] {
      opacity: 0.5; cursor: not-allowed;
    }
    button.secondary {
      padding: 0.6rem 1.25rem; border-radius: var(--kfe-radius);
      border: 1px solid var(--kfe-border);
      background: transparent; color: var(--kfe-fg);
      font: inherit; cursor: pointer;
    }
    .signed-banner {
      padding: 0.75rem 1rem; margin-bottom: 1rem;
      border-radius: var(--kfe-radius);
      background: var(--kfe-surface); color: var(--kfe-success);
      border: 1px solid var(--kfe-border);
    }
  `;

  /** @type {boolean} */ _signed = false;
  /** @type {number | null} */ _signedAt = null;
  /** @type {boolean} */ _agreed = false;
  /** @type {boolean} */ _submitting = false;

  async onConnected() {
    this._signed = (await services.learner.getSetting("personal_promise_signed")) === true;
    const at = await services.learner.getSetting("personal_promise_signed_at");
    this._signedAt = typeof at === "number" ? at : null;

    this.dispatchEvent(new CustomEvent("kfe:set-header-title", {
      detail: { title: t("personalPromise.title") },
      bubbles: true,
      composed: true,
    }));

    const onI18n = () => this._scheduleRender();
    document.addEventListener("kfe:i18n", onI18n);
    this.addCleanup(() => document.removeEventListener("kfe:i18n", onI18n));

    this._scheduleRender();
  }

  /** @param {Event} e */
  _onAgreeChange = (e) => {
    this._agreed = /** @type {HTMLInputElement} */ (e.target).checked;
    const btn = /** @type {HTMLButtonElement | null} */ (
      this.shadowRoot?.querySelector("button.primary")
    );
    if (btn) btn.disabled = !this._agreed || this._submitting;
  };

  _disabledSubmit() { return !this._agreed || this._submitting; }

  _onSubmit = async () => {
    if (!this._agreed || this._submitting) return;
    this._submitting = true;
    const now = Date.now();
    await services.learner.setSetting("personal_promise_signed", true);
    await services.learner.setSetting("personal_promise_signed_at", now);
    this._signed = true;
    this._signedAt = now;

    document.dispatchEvent(new CustomEvent("kfe:setting-changed", {
      detail: { name: "personal_promise_signed", value: true },
    }));

    router.navigate("/settings");
  };

  _onBack = () => {
    router.navigate("/settings");
  };

  /** @param {number} ts */
  _formatDate(ts) {
    return new Intl.DateTimeFormat(getLanguage(), {
      year: "numeric", month: "long", day: "numeric",
    }).format(new Date(ts));
  }

  render() {
    const items = [1, 2, 3, 4, 5, 6].map((n) => html`<li>${t(`personalPromise.item${n}`)}</li>`);

    return html`
      ${this._signed && this._signedAt != null ? html`
        <p class="signed-banner">
          ${t("personalPromise.signedOn", { date: this._formatDate(this._signedAt) })}
        </p>
      ` : ""}

      <div class="doc">
        <h2>${t("personalPromise.heading")}</h2>
        <p class="intro">${t("personalPromise.intro")}</p>
        <ol>${items}</ol>
      </div>

      ${this._signed ? html`
        <div class="actions" style="margin-top: 1rem;">
          <button class="secondary" type="button" on:click=${this._onBack}>
            ${t("personalPromise.back")}
          </button>
        </div>
      ` : html`
        <label class="agree-row">
          <input
            type="checkbox"
            .checked=${this._agreed}
            on:change=${this._onAgreeChange}
          />
          <span>${t("personalPromise.agree")}</span>
        </label>
        <div class="actions">
          <button
            class="primary"
            type="button"
            .disabled=${this._disabledSubmit()}
            on:click=${this._onSubmit}
          >${t("personalPromise.submit")}</button>
        </div>
      `}
    `;
  }
}
